import { Box, Group, HoverCard, Stack, Text, UnstyledButton } from '@mantine/core';
import { ReactNode, useState } from 'react';
import { IconExternalLink } from '@tabler/icons-react';
import { formatCalendarStyle } from '@/utils/datetime.ts';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import { RunGroup } from '@/pages/AutomationPage/components/ManualRunModal/hooks/runGroupMatching.ts';

const RunMenuItem = ({
  runId,
  runName,
  createdAt,
  getRunUrl,
  status,
  isLast,
}: {
  runId: string;
  runName: string;
  createdAt: string;
  getRunUrl: (runId: string) => string;
  status: RunGroup['latest']['run']['status'];
  isLast: boolean;
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <UnstyledButton
      p="xs"
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        boxSizing: 'border-box',
        backgroundColor: hovered ? '#FFEAF7' : 'transparent',
        borderBottom: isLast ? 'none' : '1px solid #ECECEC',
        borderRadius: hovered ? 6 : 0,
        transition: 'background-color 120ms ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(event) => {
        event.stopPropagation();
        window.open(getRunUrl(runId), '_blank', 'noopener,noreferrer');
      }}
    >
      <Group wrap="nowrap" gap="xs" w="100%" align="flex-start">
        <StatusIndicator status={status} include_text={false} />
        <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
          <Text fw={500} size="sm" lineClamp={1}>
            {runName}
          </Text>
          <Text size="xs" c="dimmed">
            {formatCalendarStyle(createdAt)}
          </Text>
        </Stack>
        <IconExternalLink
          size={13}
          color={hovered ? 'var(--velos-brand-pink)' : '#94A3B8'}
          style={{ flexShrink: 0, marginTop: 2 }}
        />
      </Group>
    </UnstyledButton>
  );
};

const RunHistoryPopover = ({
  group,
  getRunUrl,
  title,
  children,
}: {
  group: RunGroup;
  getRunUrl: (runId: string) => string;
  title: string;
  children: ReactNode;
}) => (
  <HoverCard shadow="lg" openDelay={150} withinPortal>
    <HoverCard.Target>
      <span>{children}</span>
    </HoverCard.Target>

    <HoverCard.Dropdown
      onClick={(event) => event.stopPropagation()}
      p={0}
      style={{
        width: '300px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Text size="12px" fw={600} px="md" pt="md" pb="4px">
        {title}
      </Text>

      <Box
        px="xs"
        pb="xs"
        style={{
          width: '100%',
          maxHeight: '180px',
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Stack gap={0} w="100%">
          {group.runs.map((groupRun, index) => (
            <RunMenuItem
              key={groupRun.run.id}
              runId={groupRun.run.id}
              runName={groupRun.run.name}
              createdAt={groupRun.run.created_at}
              getRunUrl={getRunUrl}
              status={groupRun.run.status}
              isLast={index === group.runs.length - 1}
            />
          ))}
        </Stack>
      </Box>
    </HoverCard.Dropdown>
  </HoverCard>
);

export default RunHistoryPopover;
